import { Check, ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";

export interface AdminSelectOption<T extends string> {
  value: T;
  label: string;
  description?: string;
}

interface AdminSelectProps<T extends string> {
  value: T;
  options: Array<AdminSelectOption<T>>;
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
}

export function AdminSelect<T extends string>({ value, options, onChange, ariaLabel, disabled = false }: AdminSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const controlRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return;

    const positionMenu = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const availableBelow = window.innerHeight - rect.bottom - 12;
      const availableAbove = rect.top - 12;
      const openAbove = availableBelow < 190 && availableAbove > availableBelow;
      const maxHeight = Math.max(150, Math.min(320, openAbove ? availableAbove - 8 : availableBelow - 8));
      setMenuStyle({
        left: rect.left,
        width: rect.width,
        maxHeight,
        ...(openAbove ? { bottom: window.innerHeight - rect.top + 8 } : { top: rect.bottom + 8 }),
      });
    };

    const closeOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!controlRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    positionMenu();
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);

  const choose = (nextValue: T) => {
    onChange(nextValue);
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <div className="admin-select-control" ref={controlRef}>
      <button
        ref={triggerRef}
        type="button"
        className="admin-select-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span>{selected?.label || "Bitte auswählen"}</span>
        <ChevronDown className={open ? "open" : ""} />
      </button>

      {open && createPortal(
        <div id={listboxId} className="admin-select-options" role="listbox" aria-label={ariaLabel} ref={menuRef} style={menuStyle}>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? "selected" : ""}
              key={option.value}
              onClick={() => choose(option.value)}
            >
              <span className="admin-select-option-marker" />
              <span><strong>{option.label}</strong>{option.description && <small>{option.description}</small>}</span>
              {option.value === value && <Check />}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
